import { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import { setAuthCallbacks } from '../api/client.js';
import { login as apiLogin, register as apiRegister, logout as apiLogout, getProfile } from '../api/auth.js';
import { getUsage } from '../api/billing.js';

const AuthContext = createContext(null);

const initialState = {
  user: null,
  refreshToken: null,
  expiresIn: null,
  subscription: null,
  isAuthenticated: false,
  isLoading: true,
};

function authReducer(state, action) {
  switch (action.type) {
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        refreshToken: action.payload.refreshToken,
        expiresIn: action.payload.expiresIn,
        isAuthenticated: true,
        isLoading: false,
      };
    case 'LOGOUT':
      return { ...initialState, isLoading: false };
    case 'REFRESH_SUCCESS':
      return {
        ...state,
        refreshToken: action.payload.refreshToken,
        expiresIn: action.payload.expiresIn,
      };
    case 'SET_SUBSCRIPTION':
      return { ...state, subscription: action.payload };
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    default:
      return state;
  }
}

export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(authReducer, initialState);

  const fetchUsage = useCallback(async () => {
    const usage = await getUsage();
    dispatch({ type: 'SET_SUBSCRIPTION', payload: usage });
  }, []);

  // Check auth status on mount
  useEffect(() => {
    let cancelled = false;
    async function checkAuth() {
      try {
        const user = await getProfile();
        if (cancelled) return;
        dispatch({
          type: 'LOGIN_SUCCESS',
          payload: { user, refreshToken: state.refreshToken, expiresIn: state.expiresIn },
        });
        await fetchUsage();
      } catch {
        if (cancelled) return;
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    }
    checkAuth();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Wire auth callbacks into API client
  useEffect(() => {
    setAuthCallbacks({
      getRefreshToken: () => state.refreshToken,
      onRefreshSuccess: (data) => {
        dispatch({
          type: 'REFRESH_SUCCESS',
          payload: { refreshToken: data.refresh_token, expiresIn: data.expires_in },
        });
      },
      onRefreshFailure: () => {
        dispatch({ type: 'LOGOUT' });
      },
    });
  }, [state.refreshToken]);

  const login = useCallback(async (email, password) => {
    const data = await apiLogin(email, password);
    dispatch({
      type: 'LOGIN_SUCCESS',
      payload: {
        user: null,
        refreshToken: data.refresh_token,
        expiresIn: data.expires_in,
      },
    });
    const user = await getProfile();
    dispatch({
      type: 'LOGIN_SUCCESS',
      payload: { user, refreshToken: data.refresh_token, expiresIn: data.expires_in },
    });
    await fetchUsage();
  }, [fetchUsage]);

  const register = useCallback(async (email, password) => {
    const data = await apiRegister(email, password);
    dispatch({
      type: 'LOGIN_SUCCESS',
      payload: {
        user: null,
        refreshToken: data.refresh_token,
        expiresIn: data.expires_in,
      },
    });
    const user = await getProfile();
    dispatch({
      type: 'LOGIN_SUCCESS',
      payload: { user, refreshToken: data.refresh_token, expiresIn: data.expires_in },
    });
    await fetchUsage();
  }, [fetchUsage]);

  const logout = useCallback(async () => {
    try {
      await apiLogout(state.refreshToken);
    } catch {
      // Logout even if the API call fails
    }
    dispatch({ type: 'LOGOUT' });
  }, [state.refreshToken]);

  const googleLogin = useCallback(() => {
    const baseUrl = import.meta.env.VITE_API_BASE_URL || '';
    window.location.href = `${baseUrl}/api/auth/google`;
  }, []);

  const handleOAuthCallback = useCallback(async (accessToken, refreshToken, expiresIn) => {
    dispatch({
      type: 'LOGIN_SUCCESS',
      payload: { user: null, refreshToken, expiresIn: parseInt(expiresIn, 10) },
    });
    const user = await getProfile();
    dispatch({
      type: 'LOGIN_SUCCESS',
      payload: { user, refreshToken, expiresIn: parseInt(expiresIn, 10) },
    });
    await fetchUsage();
  }, [fetchUsage]);

  const value = {
    ...state,
    login,
    register,
    logout,
    googleLogin,
    handleOAuthCallback,
    fetchUsage,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
}
